import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { hasAnyFieldError } from "shared/lib/json-schema/hasAnyFieldError"
import { noop } from "shared/lib/noop"
import { useEvent } from "shared/lib/useEvent"
import { ProcessStepStatus } from "shared/types/ProcessStep"
import { ProductConfiguratorStepType } from "shared/types/ProductConfiguration"
import { ProductConfiguratorMode } from "shared/types/ProductConfiguratorMode"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import {
  ProductConfiguratorProps,
  ProductConfiguratorStep
} from "../ProductConfigurator"

export type UseProductConfiguratorStepsArgs = Pick<
  ProductConfiguratorProps,
  | "schema"
  | "initialStep"
  | "mode"
  | "externalProductDimensioningModel"
  | "isReady"
  | "isLoading"
  | "isUpdating"
  | "isLoadingProductInformation"
  | "isConfigurable"
  | "addonOptions"
>

export function useProductConfiguratorSteps({
  schema,
  initialStep,
  externalProductDimensioningModel,
  isLoading,
  isUpdating,
  addonOptions,
  isConfigurable,
  isReady = false,
  mode = ProductConfiguratorMode.CONFIGURATION,
  isLoadingProductInformation
}: UseProductConfiguratorStepsArgs) {
  const { t } = useTranslation()

  const firstStep = useMemo<ProductConfiguratorStepType>(() => {
    if (initialStep) {
      return initialStep
    }
    if (
      externalProductDimensioningModel?.isExternalProductDimensioningAvailable &&
      mode === ProductConfiguratorMode.DIMENSIONING
    ) {
      return ProductConfiguratorStepType.DIMENSIONING
    }
    return ProductConfiguratorStepType.CONFIGURATION
  }, [
    initialStep,
    externalProductDimensioningModel?.isExternalProductDimensioningAvailable,
    mode
  ])

  const [activeStep, activateStep] = useState(firstStep)

  const availableSteps = useMemo<ProductConfiguratorStepType[]>(() => {
    const result = []
    if (
      externalProductDimensioningModel?.isExternalProductDimensioningAvailable &&
      mode === ProductConfiguratorMode.DIMENSIONING
    ) {
      result.push(ProductConfiguratorStepType.DIMENSIONING)
    }
    if (isConfigurable) {
      result.push(ProductConfiguratorStepType.CONFIGURATION)
    }
    if (isConfigurable && (addonOptions?.length ?? 0) > 0) {
      result.push(ProductConfiguratorStepType.ADDONS)
    }
    result.push(ProductConfiguratorStepType.SUMMARY)
    return result
  }, [
    addonOptions,
    isConfigurable,
    mode,
    externalProductDimensioningModel?.isExternalProductDimensioningAvailable
  ])

  const steps = useMemo<ProductConfiguratorStep[]>(() => {
    const result: ProductConfiguratorStep[] = []

    if (availableSteps.includes(ProductConfiguratorStepType.DIMENSIONING)) {
      result.push({
        value: ProductConfiguratorStepType.DIMENSIONING,
        label: t("Product.Configuration.Dimensioning"),
        description: t("Product.Configuration.Steps.DimensioningDescription"),
        icon: IconName.DIMENSIONING,
        status: externalProductDimensioningModel?.externalDimensioningResult
          ? undefined
          : ProcessStepStatus.SKIPPED
      })
    }
    if (availableSteps.includes(ProductConfiguratorStepType.CONFIGURATION)) {
      result.push({
        value: ProductConfiguratorStepType.CONFIGURATION,
        label: t("Product.Configuration.Configuration"),
        description: t("Product.Configuration.Steps.ConfigurationDescription"),
        icon: IconName.CONFIGURATION
      })
    }

    if (availableSteps.includes(ProductConfiguratorStepType.ADDONS)) {
      result.push({
        value: ProductConfiguratorStepType.ADDONS,
        label: t("Product.Configuration.Addon.Addons"),
        description: t("Product.Configuration.Steps.AddonsDescription"),
        icon: IconName.PUZZLE
      })
    }

    result.push({
      value: ProductConfiguratorStepType.SUMMARY,
      label: t("Product.Configuration.Summary"),
      description: t("Product.Configuration.Steps.SummaryDescription"),
      icon: IconName.ASSIGNMENT
    })

    return result
  }, [
    t,
    availableSteps,
    addonOptions,
    externalProductDimensioningModel?.externalDimensioningResult
  ])

  const handleNext = useEvent(() => {
    const currentIndex = steps.findIndex(step => step.value === activeStep)
    const nextStep = steps[currentIndex + 1]

    if (nextStep) {
      activateStep(nextStep.value)
    }
  })

  const handlePrevious = useEvent(() => {
    const currentIndex = steps.findIndex(step => step.value === activeStep)
    const previousStep = steps[currentIndex - 1]
    if (previousStep) {
      activateStep(previousStep.value)
    }
  })

  const isNextStepDisabled = useMemo<boolean>(() => {
    switch (activeStep) {
      case ProductConfiguratorStepType.DIMENSIONING: {
        return !externalProductDimensioningModel?.externalDimensioningResult
      }
      case ProductConfiguratorStepType.CONFIGURATION:
      case ProductConfiguratorStepType.ADDONS: {
        return !!isLoading || !!isUpdating || hasAnyFieldError(schema)
      }
      case ProductConfiguratorStepType.SUMMARY:
        return isLoadingProductInformation || false
      default:
        return false
    }
  }, [
    activeStep,
    schema,
    externalProductDimensioningModel,
    isReady,
    isLoadingProductInformation
  ])

  return {
    steps,
    isNextStepDisabled,
    isPreviousStepDisabled: false,

    activeStep,
    firstStep,
    activateStep: activateStep as (
      steptType: ProductConfiguratorStepType
    ) => void,
    activateNextStep: handleNext,
    activatePreviousStep: handlePrevious,
    resetExternalDimensioning: externalProductDimensioningModel?.reset ?? noop
  }
}
